import axios from 'axios';
import jwt from 'jsonwebtoken';
import Cookies, {CookieSetOptions} from 'universal-cookie';
import {PingAuthInfo, PingAuthInfoCookie} from './types/PingAuthInfo';
import PingAuthTokens from './types/PingAuthTokens';
import {PING_APPLICATION_ID, PING_AUTH_BASE_URL} from '~/utils/Constants';

export const defaultCookieSetOptions: CookieSetOptions = {
    path: '/',
};

export const isAccessTokenExpired = (authInfo: PingAuthInfo): boolean => {
    const now = new Date();
    const imminently = now.getTime() + 5000;
    // Consider it expired (causing a refresh) if the token will be expired shortly.
    // This should avoid issuing requests just prior to expiry, such that they fail.
    return imminently >= authInfo.expiryDate.getTime();
};

export const isAccessTokenValid = (authInfo: PingAuthInfo): boolean => {
    return !isAccessTokenExpired(authInfo);
};

export const readAuthInfo = (): PingAuthInfo | null => {
    const ourCookies = new Cookies();
    const pingAuthInfoCookie = ourCookies.get('authInfo') as PingAuthInfoCookie;
    if (!pingAuthInfoCookie) {
        return null;
    }
    const pingAuthInfo: PingAuthInfo = {
        tokens: pingAuthInfoCookie?.tokens,
        expiresIn: pingAuthInfoCookie?.expiresIn,
        startDate: new Date(pingAuthInfoCookie?.startDate),
        expiryDate: new Date(pingAuthInfoCookie?.expiryDate),
    };
    return pingAuthInfo;
};

const generateAuthInfo = (fields: Partial<PingAuthInfo> | PingAuthTokens): PingAuthInfo => {
    let tokenData: PingAuthTokens;
    if ('tokens' in fields) {
        // Then we have the PingAuthInfo structure, and the token fields are one level down
        tokenData = fields.tokens as PingAuthTokens;
    } else {
        // Then we have the token data as given in the callback-from-login-page URL
        tokenData = {...fields} as PingAuthTokens;
    }

    const startDate = new Date();
    const expiresIn: number = fields?.expiresIn || 0;
    let expiryDate: Date;

    if (expiresIn) {
        // If this value is specified and valid, use it to calculate an expiry
        expiryDate = new Date(startDate.getTime() + expiresIn * 1000);
        window.console.debug('Calculating a token expiry date from the specified value', {
            expiresIn,
            expiryDate,
        });
    } else {
        const accessTokenData = jwt.decode(tokenData?.accessToken);
        expiryDate = new Date(accessTokenData.exp * 1000);
        window.console.debug('Using the token expiry date from the accessToken', {
            accessTokenData,
            expiryDate,
        });
    }

    return {
        tokens: {
            accessToken: tokenData?.accessToken || '',
            refreshToken: tokenData?.refreshToken || '',
            idToken: tokenData?.idToken || '',
        },
        expiryDate,
        startDate,
        expiresIn,
    };
};

const updateTokenDebugGlobal = (authInfo: PingAuthInfo): void => {
    if (authInfo?.tokens?.accessToken) {
        const tokenData = jwt.decode(authInfo.tokens.accessToken);
        globalThis.debug = {
            ...globalThis.debug,
            tokenData,
            authInfo,
        };
    }
};

/**
 * Reaches out to the token refresh endpoint to fetch new tokens, updates the cookies, and returns the current value
 * @returns Refreshed PingAuthInfo with the new needed tokens
 */
export const refreshAccessTokenCookies = async (): Promise<PingAuthInfo> => {
    const cookies = new Cookies();
    const pingAuthInfo: PingAuthInfoCookie = cookies.get('authInfo') as PingAuthInfoCookie;
    let refreshedAuthInfo: PingAuthInfo;

    const refreshRequestParams = new URLSearchParams({
        accessToken: pingAuthInfo.tokens.accessToken,
        refreshToken: pingAuthInfo.tokens.refreshToken,
    });

    const result = await axios.post(`${PING_AUTH_BASE_URL}/tokens/refresh`, refreshRequestParams, {
        headers: {
            'x-application': PING_APPLICATION_ID,
            'Content-Type': 'application/x-www-form-urlencoded',
        },
    });
    window.console.log({
        _msg: 'DEBUG: Refreshed access token, updating cookies!',
        data: result.data,
    }); // DEBUG

    if ('accessToken' in result.data) {
        refreshedAuthInfo = generateAuthInfo(result.data);
        cookies.set('authInfo', refreshedAuthInfo, defaultCookieSetOptions);
        updateTokenDebugGlobal(refreshedAuthInfo);
    } else {
        // This should not happen; if it does, we want to be able to notice easily
        window.console.log(`Unexpected response while refreshing access tokens: ${result.data}`);
        // Just return the current (non-refreshed) auth info
        return readAuthInfo() as PingAuthInfo;
    }
    return refreshedAuthInfo;
};

export const gatherAuthInfoFromUrl = (): PingAuthInfo => {
    const currentUrl = new URL(window.location.href);
    const urlTokens: string = currentUrl.searchParams.get('tokens') || '{}';
    const parsedTokens = JSON.parse(urlTokens) as PingAuthTokens;

    return generateAuthInfo(parsedTokens);
};

export const setAuthInfoCookie = (authInfoToSave: PingAuthInfo): void => {
    const ourCookies = new Cookies();
    const setOptions = {...defaultCookieSetOptions};
    // NOTE: Inhibit expiry for now .. the expiry date we receive is for the accessToken's validity, we may still need it (and the refreshToken) after this expiry
    // if (authInfoToSave.expiryDate) {
    //     defaultCookieSetOptions.expires = authInfoToSave.expiryDate; // relative max age from when the client receives it, in seconds
    // }
    ourCookies.set('authInfo', authInfoToSave, setOptions);
};

export const removeAllCookies = (): void => {
    const ourCookies = new Cookies();
    ourCookies.remove('tokens', defaultCookieSetOptions);
    ourCookies.remove('authInfo', defaultCookieSetOptions);
};
